import { useRef, useState } from 'react'
import type { FormEvent, KeyboardEvent } from 'react'

import { Flex, InfoIcon, Link, Text } from '@buffer-mono/popcorn'

import { EmojiPickerButton } from '../../components/EngagementReplyFormFooter'
import { ReplyForm } from '../../components/ReplyForm'
import { SavedRepliesPopover } from '../../components/SavedRepliesPopover'
import type { Service } from '../../graphqlEnums'
import { instagramConversationUrl } from './replyWindow'
import { DM_CHARACTER_LIMIT, useMessageDraft } from './useMessageDraft'
import { useReplyWindowOpen } from './useReplyWindowOpen'

import styles from './MessageComposer.module.css'

import type { JSX } from 'react'

type MessageComposerProps = {
  conversationId: string
  service: Service
  /** The counterpart's handle, for the link into the Instagram app. */
  counterpartUsername: string | null
  /** When the network stops letting us reply; `null` when it does not. */
  replyWindowExpiresAt: string | null
  /**
   * Sends the message. Resolves `true` once it is sent and `false` when it
   * was not, which puts the text back in the field.
   */
  onSend: (text: string) => Promise<boolean>
  /** Reference time for the window check. Defaults to now; tests pin it. */
  now?: Date
}

/**
 * The reply form under the thread, per the DMs Figma `_input v2`: the shared
 * reply form on its card surface, with the message field and a footer of
 * emoji picker, saved replies and send.
 *
 * Enter sends and Shift+Enter starts a new line, as in most chat apps. The
 * field clears as soon as a message goes, and holds still until the send
 * settles, so a message that was not sent can come back without overwriting
 * anything typed since.
 *
 * When the network's reply window has closed the form gives way to a notice
 * that points at the conversation in the Instagram app, since Buffer can no
 * longer reply there.
 */
export function MessageComposer({
  conversationId,
  service,
  counterpartUsername,
  replyWindowExpiresAt,
  onSend,
  now,
}: MessageComposerProps): JSX.Element {
  const replyWindowOpen = useReplyWindowOpen(replyWindowExpiresAt, now)

  if (!replyWindowOpen) {
    return <ReplyWindowClosed counterpartUsername={counterpartUsername} />
  }

  // One form per conversation: the text typed for one person must never
  // render under another, so a new conversation starts from an empty draft.
  return <ComposerForm key={conversationId} service={service} onSend={onSend} />
}

function ComposerForm({
  service,
  onSend,
}: Pick<MessageComposerProps, 'service' | 'onSend'>): JSX.Element {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const draft = useMessageDraft({ service, textareaRef })
  const [sending, setSending] = useState(false)
  const canSend = !sending && draft.text.trim() !== ''

  // The footer's insertions wait for the send too: text they added while it
  // was on its way would be overwritten by a message that comes back.
  const insertEmoji = (emoji: string): void => {
    if (!sending) draft.insertEmoji(emoji)
  }
  const insertSavedReply: typeof draft.insertSavedReply = (savedReply) => {
    if (!sending) draft.insertSavedReply(savedReply)
  }

  const handleSubmit = async (
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault()
    if (!canSend) return
    const text = draft.text
    setSending(true)
    // A highlight left on an inserted saved reply would replay on text that
    // comes back after a failed send.
    draft.clearInsertedRange()
    draft.setText('')
    let sent = false
    try {
      sent = await onSend(text)
    } finally {
      if (!sent) draft.setText(text)
      setSending(false)
    }
  }

  return (
    <ReplyForm.Root
      surface="card"
      ariaLabel="Write a message"
      onSubmit={(event): void => {
        void handleSubmit(event)
      }}
    >
      <ReplyForm.Field
        textareaRef={textareaRef}
        replyText={draft.text}
        setReplyText={draft.setText}
        insertedRange={draft.insertedRange}
        onHighlightComplete={draft.clearInsertedRange}
        maxLength={DM_CHARACTER_LIMIT}
        service={service}
        ariaLabel="Message"
        placeholder="Write a message..."
        rows={1}
        readOnly={sending}
        onKeyDown={sendOnEnter}
      />
      <ReplyForm.Footer>
        <ReplyForm.FooterStart>
          <EmojiPickerButton
            emojiPickerRef={draft.emojiPickerRef}
            onEmojiSelect={insertEmoji}
          />
          <SavedRepliesPopover
            savedRepliesPopoverRef={draft.savedRepliesRef}
            handleInsertSavedReply={insertSavedReply}
          />
        </ReplyForm.FooterStart>
        <ReplyForm.FooterEnd>
          <ReplyForm.Submit label="Send" disabled={!canSend} />
        </ReplyForm.FooterEnd>
      </ReplyForm.Footer>
    </ReplyForm.Root>
  )
}

/**
 * Enter submits the form; Shift+Enter keeps its new line. An input method
 * composing a character (Japanese, Chinese, Korean) uses Enter to confirm it,
 * so that Enter must not send.
 */
function sendOnEnter(event: KeyboardEvent<HTMLTextAreaElement>): void {
  if (event.key !== 'Enter' || event.shiftKey) return
  if (event.nativeEvent.isComposing) return
  event.preventDefault()
  event.currentTarget.form?.requestSubmit()
}

/**
 * Per the Figma "Replying blocked" frame: the window Meta gives third-party
 * apps has passed, so the reply has to happen in the Instagram app. The
 * conversation stays in the inbox.
 */
function ReplyWindowClosed({
  counterpartUsername,
}: Pick<MessageComposerProps, 'counterpartUsername'>): JSX.Element {
  return (
    <Flex justify="center" className={styles.closed}>
      <Text size="sm" color="subtle" icon={<InfoIcon size="small" />}>
        7 day reply window for this conversation expired.{' '}
        {counterpartUsername && (
          <Link
            href={instagramConversationUrl(counterpartUsername)}
            external
            className={styles.closedLink}
          >
            Reply on Instagram
          </Link>
        )}
      </Text>
    </Flex>
  )
}
